import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';
import { DMRLFilterForm } from '../../../form-model/dmrl-filter-form.model';
import { firstValueFrom, takeUntil, catchError, ReplaySubject } from 'rxjs';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { ToastrService } from '../../../services/toastr-service';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { DmrlService } from '../dmrl.service';
import '@maersk-global/mds-components-core-checkbox-group';
import '@maersk-global/mds-components-core-checkbox';
import { KeyValue } from '../../../models/loc.model';

@Component({
  selector: 'app-dmrl-advance-filter',
  imports: [FormsModule, ReactiveFormsModule, AemsMultilineTagInputComponent],
  templateUrl: './dmrl-advance-filter.component.html',
  styleUrl: './dmrl-advance-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class DmrlAdvanceFilterComponent implements OnInit {
  @Input({ required: true }) dmrlFilterForm!: FormGroup<DMRLFilterForm>;
  locationCodeRegex: RegExp = /\b[a-zA-Z0-9]+\b/g;
  equipmentNumberRegex: RegExp = /\b[a-zA-Z]{4}\d{6,9}\b/g;
  profileRegex: RegExp = /\*[a-zA-Z0-9]+\b/g;
  profileList: any;
  allProfileList: any;
  equipmentGroupList: any;
  equipmentOwnerList: any;
  equipmentManufacturerList: any;
  equipmentReeferTypeList: any;
  containerTypeList: any;
  productionYears: number[] = [];
  batchSize = 50;
  batchDelay = 10;

  getProfileApiCall: APICallStatus = 'NOT_STARTED';
  getEquipmentGroupApiCall: APICallStatus = 'NOT_STARTED';
  getEquipmentOwnerApiCall: APICallStatus = 'NOT_STARTED';
  getEquipmentManufacturerApiCall: APICallStatus = 'NOT_STARTED';
  getEquipmentReeferTypeApiCall: APICallStatus = 'NOT_STARTED';
  getContainerTypeApiCall: APICallStatus = 'NOT_STARTED';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private toastrService: ToastrService,
    private appTextService: ApplicationtTextService,
    private formBuilder: FormBuilder,
    private dmrlService: DmrlService,
  ) {}

  ngOnInit() {
    this.addControlsToTheFormGroup();
    this.subscribeToMasterDataApiCalls();
    this.getProductionYears();
  }
  getProductionYears() {
    const currentYear = new Date().getFullYear();
    const pastYears = 25;
    this.productionYears = Array.from({ length: pastYears + 1 }, (_, i) => currentYear - i);
  }
  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }
  private addControlsToTheFormGroup() {
    this.dmrlFilterForm.addControl('equipmentID', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('locationCode', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('profile', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('productionYear', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('equipmentGroup', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('equipmentOwner', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('equipmentManufacturer', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('equipmentReeferType', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('containerType', this.formBuilder.control<string[] | null>(null));
    this.dmrlFilterForm.addControl('isSales', this.formBuilder.control<boolean | null>(null));
    this.dmrlFilterForm.addControl('isSurplus', this.formBuilder.control<boolean | null>(null));
    this.dmrlFilterForm.addControl('isEmpty', this.formBuilder.control<boolean | null>(null));
    this.dmrlFilterForm.addControl('applyDmrlFilter', this.formBuilder.control<boolean | null>(null));
  }
  private subscribeToMasterDataApiCalls() {
    // this.getEquipmentGroups();
    // this.getEquipmentOwners();
    // this.getEquipmentManufacturers();
    // this.getEquipmentReeferTypes();
    // this.getContainerTypes();
  }

  async getEquipmentGroups() {
    this.equipmentGroupList = await firstValueFrom(
      this.dmrlService.getAllEquipmentGroupList().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getEquipmentGroupApiCall = 'FAILED';
          this.toastrService.showToastr('Unable to get Equipment Groups', 'error');
          throw err;
        }),
      ),
    );
  }
  async getEquipmentOwners() {
    this.equipmentOwnerList = await firstValueFrom(
      this.dmrlService.getAllEquipmentOwnerList().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getEquipmentOwnerApiCall = 'FAILED';
          this.toastrService.showToastr('Unable to get Equipment Owners', 'error');
          throw err;
        }),
      ),
    );
  }
  async getEquipmentManufacturers() {
    this.equipmentManufacturerList = await firstValueFrom(
      this.dmrlService.getAllEquipmentManufacturerList().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getEquipmentManufacturerApiCall = 'FAILED';
          this.toastrService.showToastr('Unable to get Equipment Manufacturers', 'error');
          throw err;
        }),
      ),
    );
  }
  async getEquipmentReeferTypes() {
    this.equipmentReeferTypeList = await firstValueFrom(
      this.dmrlService.getAllEquipmentReeferTypeList().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getEquipmentReeferTypeApiCall = 'FAILED';
          this.toastrService.showToastr('Unable to get Equipment Reefer Types', 'error');
          throw err;
        }),
      ),
    );
  }
  async getContainerTypes() {
    this.containerTypeList = await firstValueFrom(
      this.dmrlService.getAllContainerTypeList().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getContainerTypeApiCall = 'FAILED';
          this.toastrService.showToastr(this.appTextService.unableToGetLOCDetails, 'error');
          throw err;
        }),
      ),
    );
  }
  onSalesCheckChange(value: boolean) {
    const current = this.dmrlFilterForm.get('isSales')?.value;
    if (current === value) {
      this.dmrlFilterForm.get('isSales')?.setValue(null);
    } else {
      this.dmrlFilterForm.get('isSales')?.setValue(value);
    }
  }
  onSurplusCheckChange(value: boolean) {
    const current = this.dmrlFilterForm.get('isSurplus')?.value;
    if (current === value) {
      this.dmrlFilterForm.get('isSurplus')?.setValue(null);
    } else {
      this.dmrlFilterForm.get('isSurplus')?.setValue(value);
    }
  }
  onEmptyCheckChange(value: boolean) {
    const current = this.dmrlFilterForm.get('isEmpty')?.value;
    if (current === value) {
      this.dmrlFilterForm.get('isEmpty')?.setValue(null);
    } else {
      this.dmrlFilterForm.get('isEmpty')?.setValue(value);
    }
  }
  applyFilters() {
    this.dmrlFilterForm.controls.applyDmrlFilter.setValue(true);
  }
  clearFilters() {
    this.dmrlFilterForm.reset();
  }
}
